import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, MailCheck, TriangleAlert } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshOrgs } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        setInvite(data);
      } catch (e) {
        setError(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      toast.success("Convite aceito! Bem-vindo à equipe.");
      localStorage.setItem("sapientia_org", data.organization_id);
      await refreshOrgs();
      navigate("/");
      window.location.reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAccepting(false);
    }
  };

  const goLogin = () => {
    localStorage.setItem("sapientia_invite_return", `/invite/${token}`);
    navigate("/login");
  };

  const box = (children) => (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center" data-testid="accept-invite-card">{children}</div>
    </div>
  );

  if (loading || authLoading) return box(<Loader2 className="h-6 w-6 animate-spin text-accent mx-auto" />);
  if (error) return box(<><TriangleAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" /><h1 className="text-xl font-bold">Convite indisponível</h1><p className="text-muted-foreground mt-2">{error}</p><Button className="mt-6" onClick={() => navigate("/")}>Ir para o início</Button></>);

  return box(
    <>
      <MailCheck className="h-10 w-10 text-accent mx-auto mb-3" />
      <h1 className="font-display text-2xl font-extrabold">Convite para {invite.organization_name}</h1>
      <p className="text-muted-foreground mt-2">Você foi convidado como <strong>{invite.role_label}</strong> para <strong>{invite.email}</strong>.</p>
      {user ? (
        user.email?.toLowerCase() === invite.email?.toLowerCase() ? (
          <Button className="mt-6 w-full" onClick={accept} disabled={accepting} data-testid="accept-invite-button">
            {accepting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Aceitar convite
          </Button>
        ) : (
          <p className="mt-6 text-sm text-red-500">Você está logado como {user.email}. Entre com {invite.email} para aceitar.</p>
        )
      ) : (
        <Button className="mt-6 w-full" onClick={goLogin} data-testid="accept-invite-login">Entrar / Criar conta para aceitar</Button>
      )}
    </>
  );
}
